import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Check, ShoppingBag, Download } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api
      .get(`/products/${id}`)
      .then((r) => {
        setProduct(r.data);
        setActiveImg(0);
      })
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="pt-40 pb-24 text-center" data-testid="product-not-found">
        <h1 className="font-serif-display text-4xl text-[#4A3F35]">Product not found</h1>
        <Link to="/shop" className="mt-6 inline-block text-sm uppercase tracking-[0.15em] text-[#C6A769]">
          Back to Shop
        </Link>
      </div>
    );
  }

  if (!product) {
    return <div className="pt-40 pb-24 text-center text-[#8A7D71]">Loading…</div>;
  }

  const gallery = product.gallery?.length ? product.gallery : [product.image];

  const addToCart = () => {
    addItem(product);
    toast.success(`${product.name} added to cart`);
  };

  const buyNow = () => {
    addItem(product);
    navigate("/cart");
  };

  return (
    <div data-testid="product-detail-page" className="pt-28 pb-10">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Link
          to="/shop"
          data-testid="back-to-shop"
          className="inline-flex items-center gap-2 text-sm text-[#8A7D71] hover:text-[#C6A769] transition-colors mb-10"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Shop
        </Link>

        <div className="grid lg:grid-cols-2 gap-14">
          <div>
            <div className="aspect-[4/5] overflow-hidden rounded-sm bg-[#FAF7F2]">
              <img
                src={gallery[activeImg]}
                alt={product.name}
                className="w-full h-full object-cover"
                data-testid="product-main-image"
              />
            </div>
            {gallery.length > 1 && (
              <div className="flex gap-4 mt-4">
                {gallery.map((g, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImg(i)}
                    data-testid={`thumbnail-${i}`}
                    className={`w-20 h-24 overflow-hidden rounded-sm transition-all duration-300 ${
                      activeImg === i ? "ring-2 ring-[#C6A769]" : "opacity-60 hover:opacity-100"
                    }`}
                  >
                    <img src={g} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="lg:pt-6">
            <span className="text-[10px] uppercase tracking-[0.2em] bg-[#E8CFCB]/60 text-[#4A3F35] px-3 py-1 rounded-sm">
              {product.category}
            </span>
            <h1 className="font-serif-display text-4xl sm:text-5xl text-[#4A3F35] mt-5 leading-tight">
              {product.name}
            </h1>
            <p className="text-[#8A7D71] italic mt-2">{product.tagline}</p>
            <p className="font-serif-display text-3xl text-[#4A3F35] mt-6">${product.price}</p>

            <p className="text-base text-[#8A7D71] leading-relaxed mt-7">{product.description}</p>

            <ul className="mt-8 space-y-3">
              {product.highlights?.map((h, i) => (
                <li key={i} className="flex items-center gap-3 text-sm text-[#4A3F35]">
                  <Check className="w-4 h-4 text-[#C6A769] shrink-0" strokeWidth={2} />
                  {h}
                </li>
              ))}
            </ul>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <button
                onClick={buyNow}
                data-testid="buy-now-button"
                className="flex-1 bg-[#4A3F35] text-[#FAF7F2] px-8 py-4 rounded-sm text-sm uppercase tracking-[0.15em] hover:bg-[#C6A769] transition-colors duration-500"
              >
                Buy Now
              </button>
              <button
                onClick={addToCart}
                data-testid="add-to-cart-button"
                className="flex-1 border border-[#4A3F35] text-[#4A3F35] px-8 py-4 rounded-sm text-sm uppercase tracking-[0.15em] hover:bg-[#4A3F35] hover:text-[#FAF7F2] transition-colors duration-500 inline-flex items-center justify-center gap-2"
              >
                <ShoppingBag className="w-4 h-4" /> Add to Cart
              </button>
            </div>

            <div className="mt-8 flex items-center gap-2 text-sm text-[#8A7D71]">
              <Download className="w-4 h-4 text-[#C6A769]" />
              Instant digital download after payment.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
